import { Button, Drawer, Gap, Icon, LinkButton, P } from '@salecycle/ui-component-library';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SendEmailThumbnail } from './SendEmailThumbnail';
import { useSendEmail } from './hooks/useSendEmail';
import { SubjectLineSelect } from './components/SubjectLineSelect';
import { SendEmailName } from './components/SendEmailName';
import { OnsiteTemplateSelectorModal } from '../../OnsiteTemplateSelectorModal';

const ThumbnailTemplateArea = styled.div`
  display: flex;
  flex-direction: horizontal;
  align-items: center;
  justify-content: space-between;
`;

const SendEmailCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: none;
  width: auto;
  padding: 24px;
  margin-top: 24px;
`;

export interface SendEmailDrawerProps {
  open: boolean;
  closeDrawer: () => void;
  messageId: string;
}

export const SendEmailDrawer = ({ open, closeDrawer, messageId }: SendEmailDrawerProps) => {
  const { t } = useTranslation('campaigns');
  const { getTemplate, getSubjectLine, navigateToEmailTemplates } = useSendEmail();

  const template = useMemo(() => getTemplate(messageId), [getTemplate, messageId]);
  const subjectLine = useMemo(() => getSubjectLine(messageId), [getSubjectLine, messageId]);

  const [isTemplateSelectorModalOpen, setIsTemplateSelectorModalOpen] = useState(false);

  const openTemplateSelectorModal = useCallback(() => setIsTemplateSelectorModalOpen(true), []);
  const closeTemplateSelectorModal = useCallback(() => setIsTemplateSelectorModalOpen(false), []);

  const hasSelectedContent = useMemo(() => template?.id || template?.name, [template?.id, template?.name]);

  return (
    <Drawer
      header={t('onsite.editor.workflow.sendEmailMessage.drawer.heading')}
      footer={
        <Button
          variation="secondary"
          fullWidth
          onClick={closeDrawer}
          ariaLabel={t('onsite.editor.workflow.sendEmailMessage.drawer.closeButtonAria')}
        >
          {t('onsite.editor.workflow.sendEmailMessage.drawer.closeButton')}
        </Button>
      }
      onClose={closeDrawer}
      open={open}
    >
      <SendEmailName id={messageId} key={messageId} />
      <SendEmailCard>
        <P>Which template should be sent?</P>
        {!hasSelectedContent && (
          <>
            <Gap bottom={16} />
            <div style={{ width: '113px' }}>
              <Button variation="primary" onClick={openTemplateSelectorModal}>
                Select Template
              </Button>
            </div>
          </>
        )}
        {hasSelectedContent && (
          <>
            <Gap bottom={16} />
            <ThumbnailTemplateArea>
              <div style={{ flexDirection: 'column' }}>
                <P>{template?.name}</P>
                <Gap bottom={8} />
                <Button variation="secondary" onClick={openTemplateSelectorModal}>
                  Change template
                </Button>
              </div>

              <div style={{ flexDirection: 'column' }}>
                <SendEmailThumbnail id={messageId} />
                <Gap bottom={8} />
                <LinkButton
                  ariaLabel={t('onsite.editor.workflow.sendEmailMessage.drawer.editTemplate')}
                  onClick={navigateToEmailTemplates(false)}
                >
                  {t('onsite.editor.workflow.sendEmailMessage.drawer.editTemplate')}
                  <Gap right={4} />
                  <div style={{ position: 'relative', margin: 'auto 0', top: '0', bottom: '0' }}>
                    <Icon icon="external-link-square" />
                  </div>
                </LinkButton>
              </div>
            </ThumbnailTemplateArea>
          </>
        )}
      </SendEmailCard>
      <Gap bottom={24} />
      <SendEmailCard>
        <Gap top={16} />
        <SubjectLineSelect id={messageId} />
        {/* negative margin added due to select component: known issue adding ghost padding */}
        <div style={{ marginTop: '-20px' }} />

        {!!subjectLine?.id && (
          <LinkButton
            ariaLabel={t('onsite.editor.workflow.sendEmailMessage.drawer.editSubjectLines')}
            onClick={navigateToEmailTemplates()}
          >
            {t('onsite.editor.workflow.sendEmailMessage.drawer.editSubjectLines')}
            <Gap right={4} />
            <div style={{ position: 'relative', margin: 'auto 0', top: '0', bottom: '0' }}>
              <Icon icon="external-link-square" />
            </div>
          </LinkButton>
        )}
      </SendEmailCard>

      <OnsiteTemplateSelectorModal
        messageId={messageId}
        onClose={closeTemplateSelectorModal}
        open={isTemplateSelectorModalOpen}
        location="sendEmail"
      />
    </Drawer>
  );
};
